import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { CKEditor } from "ckeditor4-react";
import { useDispatch, useSelector } from "react-redux";
import { getData, postData, putData } from "actions";
import {
  API_URL_department,
  API_URL_email,
  API_URL_slaplan,
  API_URL_ticketPriority,
} from "constants/api";
import { departmentReducers } from "redux/departmentSlice";
import { ticketPriorityReducers } from "redux/ticketPrioritySlice";
import { useFormik } from "formik";
import * as Yup from "yup";
import { emailReducers } from "redux/emailSlice";
import { AuthContext } from "context/AuthContext";
import { slaplanReducers } from "redux/slaplanSlice";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const FormEmails = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);
  const [visiblePassword, setVisiblePassword] = useState(false);

  const {
    getDepartmentResult,
    getTicketPriorityResult,
    getSlaplanResult,
  } = useSelector((state) => ({
    getDepartmentResult: state.department.getDepartmentResult,
    getTicketPriorityResult: state.ticketPriority.getTicketPriorityResult,
    getSlaplanResult: state.slaplan.getSlaplanResult,
  }));

  const getDepartment = useCallback(
    async (params) => {
      getData(
        API_URL_department,
        params,
        { dispatch, redux: departmentReducers },
        "GET_DEPARTMENT"
      );
    },
    [dispatch]
  );
  const getPriority = useCallback(
    async (params) => {
      getData(
        API_URL_ticketPriority,
        params,
        { dispatch, redux: ticketPriorityReducers },
        "GET_TICKETPRIORITY"
      );
    },
    [dispatch]
  );
  const getSla = useCallback(
    async (params) => {
      getData(
        API_URL_slaplan,
        params,
        { dispatch, redux: slaplanReducers },
        "GET_SLAPLAN"
      );
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues: {
      email_address: location.state ? location.state.item.email_address : "",
      email_name: location.state ? location.state.item.email_name : "",
      user_name: location.state ? location.state.item.user_name : "",
      password: "",
      fetching_host: location.state ? location.state.item.fetching_host : "",
      fetching_port: location.state ? location.state.item.fetching_port : "",
      fetching_protocol: location.state
        ? location.state.item.fetching_protocol
        : "",
      fetching_encryption: location.state
        ? location.state.item.fetching_encryption
        : "",
      fetching_validate: location.state
        ? location.state.item.fetching_validate
        : 0,
      fetching_authentication: location.state
        ? location.state.item.fetching_authentication
        : "",
      mailbox_protocol: location.state
        ? location.state.item.mailbox_protocol
        : "",
      imap_config: location.state ? location.state.item.imap_config : "",
      folder: location.state ? location.state.item.folder : "",
      sending_host: location.state ? location.state.item.sending_host : "",
      sending_port: location.state ? location.state.item.sending_port : "",
      sending_protocol: location.state
        ? location.state.item.sending_protocol
        : "",
      sending_encryption: location.state
        ? location.state.item.sending_encryption
        : "",
      sending_validate: location.state
        ? location.state.item.sending_validate
        : 0,
      sending_authentication: location.state
        ? location.state.item.sending_authentication
        : "",
      internal_notes: location.state ? location.state.item.internal_notes : "",
      auto_response: location.state ? location.state.item.auto_response : 0,
      fetching_status: location.state ? location.state.item.fetching_status : 0,
      department: location.state ? location.state.item.department : "",
      priority: location.state ? location.state.item.priority : "",
      sla: location.state ? location.state.item.sla : "",
      is_default: location.state ? location.state.item.is_default : 0,
      status: location.state ? location.state.item.status : 1,
    },
    validationSchema: Yup.object({
      email_address: Yup.string().required("Name is required"),
      email_name: Yup.string().required("From Name is required"),
      password: Yup.string().required("password is required"),
      priority: Yup.string().required("Priority is required"),
      sla: Yup.string().required("Sla Plan is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      if (location.state === null) {
        postData(
          API_URL_email,
          formData,
          { dispatch, redux: emailReducers },
          "ADD_EMAIL",
          () => {
            navigate("/administrator/email/emails");
          }
        );
      } else {
        putData(
          `${API_URL_email}${location.state.item.id}/`,
          formData,
          { dispatch, redux: emailReducers },
          "ADD_EMAIL",
          () => {
            navigate("/administrator/email/emails");
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  useEffect(() => {
    const params = `?limit=${9999999}&all=1`;
    getDepartment(params);
    getPriority(params);
    getSla(params);
  }, []);

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={typeForm === "create" ? "Create" : "Edit"}
        excludedPaths={["email", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow text-xs md:text-base">
        <form onSubmit={handleFormSubmit}>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            Email information and settings
          </div>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-4">
              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Email address <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="email"
                    name="email_address"
                    id="email_address"
                    value={formik.values.email_address}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.email_address &&
                    formik.errors.email_address && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.email_address}
                      </div>
                    )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                <label className="text-xs md:-text-sm lg:text-sm">
                  Username
                  <span className="ml-1 text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="user_name"
                    id="user_name"
                    value={formik.values.user_name}
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.user_name && formik.errors.user_name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.user_name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  From Name <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="email_name"
                    id="email_name"
                    value={formik.values.email_name}
                    ref={(ref) => (inputRefs.current[2] = ref)}
                    onChange={formik.handleChange}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.email_name && formik.errors.email_name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.email_name}
                    </div>
                  )}
                </div>
              </div>

              <div className="relative col-span-full mb-1.5 md:col-span-6 md:col-start-7 lg:col-span-6 lg:col-start-7">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Password <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type={visiblePassword ? "text" : "password"}
                    name="password"
                    id="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[3] = ref)}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  <div
                    className="absolute text-gray-400 top-[22px] md:top-[30px] right-2 text-2xl cursor-pointer"
                    onClick={() => setVisiblePassword(!visiblePassword)}
                  >
                    {visiblePassword ? <FaEye /> : <FaEyeSlash />}
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.password}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-neutral dark:bg-neutral-800 rounded shadow my-6">
              <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
                New ticket from email settings
              </div>

              <div className="mt-4 grid grid-cols-12 px-3 gap-3">
                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-4 lg:col-start-1">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Department
                  </label>
                  <div>
                    <select
                      id="department"
                      name="department"
                      value={formik.values.department}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[4] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select Department</option>
                      {getDepartmentResult.results &&
                        getDepartmentResult.results.map((item, index) => (
                          <option key={index} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </select>
                    {formik.touched.department && formik.errors.department && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.department}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-4 lg:col-start-5">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Priority
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <select
                      id="priority"
                      name="priority"
                      value={formik.values.priority}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[5] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select Priority</option>
                      {getTicketPriorityResult.results &&
                        getTicketPriorityResult.results.map((item, index) => (
                          <option key={index} value={item.id}>
                            {item.priority}
                          </option>
                        ))}
                    </select>
                    {formik.touched.priority && formik.errors.priority && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.priority}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-9 lg:col-span-4 lg:col-start-9">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Sla Plan
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <select
                      id="sla"
                      name="sla"
                      value={formik.values.sla}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[6] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select Sla Plan</option>
                      {getSlaplanResult.results &&
                        getSlaplanResult.results.map((item, index) => (
                          <option key={index} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                    </select>
                    {formik.touched.sla && formik.errors.sla && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.sla}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-neutral dark:bg-neutral-800 rounded shadow my-6">
              <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
                Incoming email information
              </div>

              <div className="mt-4 grid grid-cols-12 px-3 pb-6 gap-3">
                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-3 lg:col-start-1">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Protocol
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <select
                      id="fetching_protocol"
                      name="fetching_protocol"
                      value={formik.values.fetching_protocol}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[7] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select</option>
                      <option value="imap">IMAP</option>
                    </select>
                  </div>
                </div>

                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-3 lg:col-start-4">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Host name
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <input
                      type="text"
                      name="fetching_host"
                      id="fetching_host"
                      value={formik.values.fetching_host}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[8] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-9 lg:col-span-3 lg:col-start-7">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Port number
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <input
                      type="text"
                      name="fetching_port"
                      id="fetching_port"
                      value={formik.values.fetching_port}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[9] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-3 lg:col-start-10">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Encryption
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <select
                      id="fetching_encryption"
                      name="fetching_encryption"
                      value={formik.values.fetching_encryption}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[10] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select</option>
                      <option value="ssl">SSL</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-neutral dark:bg-neutral-800 rounded shadow my-6 pb-6">
              <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
                Outgoing email information
              </div>

              <div className="mt-4 grid grid-cols-12 px-3 gap-3">
                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-3 lg:col-start-1">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Transfer Protocol
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <select
                      id="sending_protocol"
                      name="sending_protocol"
                      value={formik.values.sending_protocol}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[11] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select</option>
                      <option value="smtp">SMTP</option>
                    </select>
                  </div>
                </div>

                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-3 lg:col-start-4">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Host name
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <input
                      type="text"
                      name="sending_host"
                      id="sending_host"
                      value={formik.values.sending_host}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[12] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-9 lg:col-span-3 lg:col-start-7">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Port number
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <input
                      type="text"
                      name="sending_port"
                      id="sending_port"
                      value={formik.values.sending_port}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[13] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-3 lg:col-start-10">
                  <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                    Encryption
                    <span className="ml-1 text-red-500">*</span>
                  </label>
                  <div>
                    <select
                      id="sending_encryption"
                      name="sending_encryption"
                      value={formik.values.sending_encryption}
                      onChange={formik.handleChange}
                      ref={(ref) => (inputRefs.current[14] = ref)}
                      className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                    >
                      <option value="">Select</option>
                      <option value="ssl">SSL</option>
                      {/* <option value="tls">TLS</option>
                      <option value="starttls">STARTTLS</option> */}
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid px-3 grid-cols-1">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm mb-1">
                  Internal notes
                </label>
                <div>
                  <CKEditor
                    config={toolbarConfig}
                    type="classic"
                    initData={formik.values.internal_notes}
                    onChange={(e) =>
                      formik.setFieldValue("internal_notes", e.editor.getData())
                    }
                  />
                </div>
              </div>

              <div className="mt-4 grid px-3 grid-cols-1">
                <label className="flex items-center gap-1 text-xs md:-text-sm lg:text-sm">
                  <input
                    id="status"
                    name="status"
                    type="checkbox"
                    checked={formik.values.status}
                    onChange={() =>
                      formik.setFieldValue(
                        "status",
                        formik.values.status === 1 ? 0 : 1
                      )
                    }
                  />
                  Active Email
                </label>
              </div>

              <div className="mt-4 grid px-3 grid-cols-1">
                <label className="flex items-center gap-1 text-xs md:-text-sm lg:text-sm">
                  <input
                    id="is_default"
                    name="is_default"
                    type="checkbox"
                    checked={formik.values.is_default}
                    onChange={() =>
                      formik.setFieldValue(
                        "is_default",
                        formik.values.is_default === 1 ? 0 : 1
                      )
                    }
                  />
                  Make default the primary email
                </label>
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormEmails;
